import { subscribeToInboxPlusRowNotifications } from '../actions/subscribeToInboxPlusRowNotifications';
import { lazyGetChannelId, NotificationSubscription, lazySubscribe } from 'owa-notification';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import SubscriptionParameters from 'owa-service/lib/contract/SubscriptionParameters';
import { getUserConfiguration } from 'owa-session-store';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import * as findConversationShapeUtil from 'owa-mail-store/lib/services/utils/findConversationShapeUtil';
import NotificationPayloadBase from 'owa-service/lib/contract/NotificationPayloadBase';
import { MailFolderTableQuery, getSelectedTableView } from 'owa-mail-list-store';
import { getInboxTableView } from 'owa-mail-table-loading-actions';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import { getConversationSortByProperty, getMailItemSortByProperty } from 'owa-mail-list-actions';
import { orchestrator } from 'satcheljs';
import getInboxPlusFolderChangeDigest from '../services/getInboxPlusFolderChangeDigestService';
import GetFolderChangeDigestResponse from 'owa-service/lib/contract/GetFolderChangeDigestResponse';
import { trace } from 'owa-trace';
import FolderChangeDigestResponse from 'owa-service/lib/contract/FolderChangeDigestResponse';
import onFolderChangeDigestResponse from '../actions/onFolderChangeDigestResponse';
import InboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/store/schema/InboxClassificationFilter';
import inboxPlusStore from 'owa-mail-inbox-plus-store/lib/store/Store';
import InboxPlusPivotViewState from 'owa-mail-inbox-plus-store/lib/store/schema/InboxPlusPivotViewState';
import { throttle } from 'lodash-es';

const SERVICE_CALL_THROTTLE_LIMIT = 60000;

orchestrator(subscribeToInboxPlusRowNotifications, actionMessage => {
    const inboxTableView = getInboxTableView(
        FocusedViewFilter.None,
        InboxClassificationFilter.None
    );
    const mailFolderTableQuery = inboxTableView.tableQuery as MailFolderTableQuery;

    lazyGetChannelId.import().then(getChannelId => {
        const subscriptionParameters: SubscriptionParameters = {
            NotificationType: 'RowNotification',
            FolderId: inboxTableView.tableQuery.folderId,
            IsConversation:
                getUserConfiguration().UserOptions.GlobalListViewTypeReact ===
                ReactListViewType.Conversation,
            ChannelId: getChannelId(),
        };

        if (subscriptionParameters.IsConversation) {
            subscriptionParameters.ConversationShapeName = findConversationShapeUtil.getFindConversationShape(
                folderNameToId('inbox'),
                false /* isFavoritesSearchFolderScenario */
            );
            subscriptionParameters.ConversationResponseShape = findConversationShapeUtil.getFindConversationResponseShape(
                folderNameToId('inbox')
            );
            subscriptionParameters.SortBy = getConversationSortByProperty(mailFolderTableQuery);
        } else {
            subscriptionParameters.SortBy = getMailItemSortByProperty(mailFolderTableQuery);
        }

        const subscription: NotificationSubscription = {
            subscriptionId: 'INBOX_PLUS_ROW_NOTIFICATIONS',
            requiresExplicitSubscribe: true,
            subscriptionParameters: subscriptionParameters,
        };

        // subscribe and store the subscription object
        lazySubscribe.importAndExecute(subscription, handleInboxPlusRowNotifications);

        getFolderChangeDigestForAllPivots();
    });
});

function handleInboxPlusRowNotifications(notification: NotificationPayloadBase): void {
    if (notification.EventType === 'RowAdded' || notification.EventType === 'RowModified') {
        getFolderChangeDigestForAllPivots();
    }
}

export function getFolderChangeDigestForAllPivots() {
    const selectedTableView = getSelectedTableView();

    if (selectedTableView == null) {
        return;
    }

    const tableQuery = selectedTableView.tableQuery as MailFolderTableQuery;

    const pivotsToUpdate = inboxPlusStore.inboxPlusPivots.filter(
        pivot =>
            pivot.isEnabled &&
            pivot.InboxClassificationFilter !== tableQuery.inboxClassificationFilter
    );

    if (pivotsToUpdate.length === 0) {
        return;
    }

    getInboxPlusFolderChangeDigestThrottled(pivotsToUpdate);
}

let getInboxPlusFolderChangeDigestThrottled = throttle(
    (pivotsToUpdate: InboxPlusPivotViewState[]) => {
        getInboxPlusFolderChangeDigest(pivotsToUpdate).then(response =>
            processResponse(pivotsToUpdate, response)
        );
    },
    SERVICE_CALL_THROTTLE_LIMIT
);

// Process GetFolderChangeDigestResponse
function processResponse(
    pivotsToUpdate: InboxPlusPivotViewState[],
    response: GetFolderChangeDigestResponse
) {
    let selectedPivot = null;
    const selectedTableView = getSelectedTableView();

    if (response.Folders == null || response.Folders.length == 0) {
        // GetFolderChangeDigestResponse.Folders can be null in case of server exception while processing the request
        trace.info('GetFolderChangeDigestResponse.Folders is null or empty');
        return;
    }

    if (selectedTableView && folderNameToId('inbox') === selectedTableView.tableQuery.folderId) {
        const tableQuery = selectedTableView.tableQuery as MailFolderTableQuery;
        selectedPivot = tableQuery.inboxClassificationFilter;
    }

    const responseFolders: FolderChangeDigestResponse[] = response.Folders;

    for (let i = 0; i < pivotsToUpdate.length; i++) {
        // If we get a folderChangeDigest response for the current selected pivot, the response has stale data
        if (selectedPivot === pivotsToUpdate[i].InboxClassificationFilter) {
            continue;
        }
        // Update store state with response data
        onFolderChangeDigestResponse(
            pivotsToUpdate[i].InboxClassificationFilter,
            responseFolders[i].UnseenCount,
            responseFolders[i].RecentUniqueSenders
        );
    }
}
